import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  RotatingText,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

export const CheckoutAnimationView: React.FC<{
  className?: string;
  ref: React.Ref<HTMLDivElement>;
}> = ({ref, className}) => {
  const {t} = useTranslation();

  return (
    <Container ref={ref} className={className} data-testid="checkout-animation">
      <IconAnimationWrapper>
        <ShirtsIcon asset={GenericIconName.Shirts} />
        <ConfettiIcon asset={GenericIconName.Confetti} />
        <OpenedBoxIcon asset={GenericIconName.OpenedBox} />
      </IconAnimationWrapper>

      <StyledRotatingText
        size="px14"
        color={'Text.Grey'}
        interval={3000}
        transitionDuration={1}
        labels={[
          t('Securing your order… just a moment!'),
          t('Checking the inventory one last time'),
          t('Processing… almost ready to pay'),
          t('Loading checkout now!'),
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 75vh;
  gap: 0;
  padding: 20px;
`;

const IconAnimationWrapper = styled.div`
  width: 72px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  top: 40%;
`;

const ShirtsIcon = styled(Icon)`
  width: 48px;
  height: 48px;
  position: absolute;
  top: 0;
  left: 50%;
  animation: shirtFirstDrop 3.5s ease-in forwards,
    shirtDropLoop 3.5s ease-in 3.5s infinite;

  @keyframes shirtFirstDrop {
    0% {
      transform: translate(-50%, 10px) rotate(0deg);
    }
    10%,
    15% {
      transform: translate(-50%, 5px) rotate(0deg);
    }
    35%,
    100% {
      transform: translate(-50%, 120px) rotate(-30deg);
    }
  }

  @keyframes shirtDropLoop {
    0% {
      transform: translate(-50%, -40px) rotate(-21deg);
    }
    10% {
      transform: translate(-50%, 10px) rotate(0deg);
    }
    14%,
    18% {
      transform: translate(-50%, 5px) rotate(0deg);
    }
    20% {
      transform: translate(-50%, 10px) rotate(0deg);
    }
    35%,
    100% {
      transform: translate(-50%, 120px) rotate(-30deg);
    }
  }
`;

const ConfettiIcon = styled(Icon)`
  width: 72px;
  height: 40px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform-origin: center bottom;
  transform: translate(-50%, 0px) scale(0.3);
  animation: confettiFirstBurst 3.5s ease-in forwards,
    confettiBurstLoop 3.5s ease-in 3.5s infinite;

  @keyframes confettiFirstBurst {
    0%,
    35% {
      transform: translate(-50%, 0px) scale(0.3);
    }
    50% {
      transform: translate(-50%, -42px) scale(1);
    }
    60%,
    70% {
      transform: translate(-50%, -40px) scale(0.95);
    }
    75% {
      transform: translate(-50%, -42px) scale(1);
    }
    85%,
    100% {
      transform: translate(-50%, 0px) scale(0.3);
    }
  }

  @keyframes confettiBurstLoop {
    0%,
    30% {
      transform: translate(-50%, 0px) scale(0.3);
    }
    50% {
      transform: translate(-50%, -42px) scale(1);
    }
    58%,
    70% {
      transform: translate(-50%, -40px) scale(0.95);
    }
    75%,
    78% {
      transform: translate(-50%, -42px) scale(1);
    }
    88%,
    100% {
      transform: translate(-50%, 0px) scale(0.3);
    }
  }
`;

const OpenedBoxIcon = styled(Icon)`
  width: 72px;
  height: 40px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const StyledRotatingText = styled(RotatingText)`
  position: absolute;
  top: calc(40% + 115px);
  line-height: 4;

  &&& {
    span {
      color: ${COLORS.Black};
      font-weight: ${FONT_WEIGHTS.w500};
    }
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 22px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: -2px;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 1) 100%
    );
  }

  &::after {
    bottom: -2px;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0)
    );
  }
`;
